"use client";
import React, { createContext, useContext, useState } from "react";

// Define initial state structure
const initialState = {
  isMapView: false,
  idOnMarker: "",
  idOnCard: "",
  place: undefined,
};

// Define the context interface
interface IBrowseContext {
  serviceSlug?: string;
  isMapView: boolean;
  idOnMarker: string;
  idOnCard: string;
  place: any | undefined; // Adjust as needed
  setMarker: (id: string) => void;
  removeMarker: () => void;
  setCard: (id: string) => void;
  removeCard: () => void;
  setPlace: (place: any) => void; // Adjust type as needed
  clearPlace: () => void;
  changeMapView: (status: boolean) => void;
}

// Create the context
export const BrowseContext = createContext({
  ...initialState,
  setMarker: () => {},
  removeMarker: () => {},
  setCard: () => {},
  removeCard: () => {},
  setPlace: () => {},
  clearPlace: () => {},
  changeMapView: () => {},
} as IBrowseContext);

// Provider component
export const BrowseContextProvider = ({
  children,
  serviceSlug,
}: {
  children: React.ReactNode;
  serviceSlug?: string;
}) => {
  const [state, setState] = useState(initialState);

  // Set marker
  const setMarker = (id: string) => {
    setState((prev) => ({
      ...prev,
      idOnMarker: id,
    }));
  };

  // Remove marker
  const removeMarker = () => {
    setState((prev) => ({
      ...prev,
      idOnMarker: "",
    }));
  };

  // Set card
  const setCard = (id: string) => {
    setState((prev) => ({
      ...prev,
      idOnCard: id,
    }));
  };

  // Remove card
  const removeCard = () => {
    setState((prev) => ({
      ...prev,
      idOnCard: "",
    }));
  };

  // Set place
  const setPlace = (place: any) => {
    setState((prev) => ({
      ...prev,
      place,
    }));
  };

  // Clear place
  const clearPlace = () => {
    setState((prev) => ({
      ...prev,
      place: undefined,
    }));
  };

  const changeMapView = (status: boolean) => {
    setState((prev) => ({
      ...prev,
      isMapView: status,
    }));
  };

  return (
    <BrowseContext.Provider
      value={{
        ...state,
        serviceSlug,
        setMarker,
        removeMarker,
        setCard,
        removeCard,
        setPlace,
        clearPlace,
        changeMapView,
      }}
    >
      {children}
    </BrowseContext.Provider>
  );
};

// Hook to use the context
export const useBrowse = () => useContext(BrowseContext);
